import { Link, useSearchParams } from 'react-router-dom'
import { PageHeader, Panel, Tabs, StatTile, DataTable, Chip, EmptyState, type Column } from '../ds/index'
import { useApi } from '../lib/useApi'
import { ago, dateLong, money, num, timeShort } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { LeadFilterBar, useLeadFilters, useFilterOptions } from '../components/LeadFilters'
import { RateBar } from '../components/Viz'
import { duration } from './Dashboard'

type Tab = 'timeline' | 'sold' | 'pairs'
type Tone = 'pos' | 'warn' | 'neg' | 'accent' | undefined

type Event = {
  at: string; type: string; post_id: string | null; rep_id: string | null; rep_name: string | null
  buyer_id: string | null; buyer_name: string | null; cents: number | null; detail: string | null
  summary: string | null; text_start: string | null; category_name: string | null; area_name: string | null; group_name: string | null
}

// The loop, in order. Each type says what happened in plain words.
const TYPES: { key: string; label: string; tone?: Tone }[] = [
  { key: 'alert', label: 'Muse alerts' },
  { key: 'held', label: 'Held alerts', tone: 'warn' },
  { key: 'lead_found', label: 'Leads found', tone: 'accent' },
  { key: 'not_lead', label: 'Not leads' },
  { key: 'no_buyer', label: 'No buyer', tone: 'warn' },
  { key: 'sent', label: 'Sent to rep' },
  { key: 'opened', label: 'Rep opened' },
  { key: 'replied', label: 'Rep replied', tone: 'pos' },
  { key: 'skipped', label: 'Rep skipped', tone: 'warn' },
  { key: 'sold', label: 'Sold', tone: 'pos' },
  { key: 'buyer_emailed', label: 'Buyer emailed' },
  { key: 'buyer_answered', label: 'Buyer answered' },
]
// Without a choice the timeline leaves out the noise: every raw alert and every non-lead.
const DEFAULT_TYPES = TYPES.map((t) => t.key).filter((k) => k !== 'alert' && k !== 'not_lead')

function sentence(e: Event) {
  const rep = e.rep_name || 'A rep'
  switch (e.type) {
    case 'alert': return <>Muse alert{e.rep_name ? <> from <strong>{e.rep_name}</strong></> : e.detail ? <> from {e.detail}</> : null}{e.group_name ? <> in {e.group_name}</> : null}</>
    case 'held': return <>Alert held: <strong>{e.detail}</strong> is not tied to a rep yet</>
    case 'lead_found': return <>Lead found{e.category_name ? <>: <strong>{e.category_name}</strong></> : null}{e.area_name ? <> in {e.area_name}</> : null}</>
    case 'not_lead': return <>Screened out, not a request</>
    case 'no_buyer': return <>Nobody to send it to{e.detail ? <>: {e.detail}</> : null}</>
    case 'sent': return <>Sent to <strong>{rep}</strong></>
    case 'opened': return <><strong>{rep}</strong> opened it</>
    case 'replied': return <><strong>{rep}</strong> replied on Facebook{e.detail ? <>: “{e.detail}”</> : null}</>
    case 'skipped': return <><strong>{rep}</strong> skipped it{e.detail ? <>: {e.detail}</> : null}</>
    case 'sold': return <>Sold to <strong>{e.buyer_name}</strong>{e.cents != null ? <> for {money(e.cents)}</> : <> (no price set)</>}, via {rep}</>
    case 'buyer_emailed': return <><strong>{e.buyer_name}</strong> was emailed the recommendation</>
    case 'buyer_answered': return <><strong>{e.buyer_name}</strong> says the person {e.detail === 'contacted' ? 'reached out' : 'has not reached out yet'}</>
    default: return e.type
  }
}

function Timeline({ query, types, setTypes }: { query: string; types: string[]; setTypes: (t: string[]) => void }) {
  const { data, error } = useApi<{ events: Event[]; counts: Record<string, number>; truncated: boolean }>(`/api/activity?${query}&types=${types.join(',')}`)
  const toggle = (k: string) => setTypes(types.includes(k) ? types.filter((t) => t !== k) : [...types, k])
  if (error) return <ErrorBlock error={error} />
  // Grouped by day so a week reads as a week.
  const days: { day: string; events: Event[] }[] = []
  for (const e of data?.events || []) {
    const d = dateLong(e.at)
    if (days[days.length - 1]?.day !== d) days.push({ day: d, events: [] })
    days[days.length - 1].events.push(e)
  }
  const tone = (k: string) => TYPES.find((t) => t.key === k)?.tone
  return (
    <>
      <div className="type-chips" role="group" aria-label="What to show">
        {TYPES.map((t) => (
          <button key={t.key} className={`type-chip${types.includes(t.key) ? ' on' : ''}`} aria-pressed={types.includes(t.key)} onClick={() => toggle(t.key)}>
            {t.label} <span className="num">{data?.counts[t.key] ?? 0}</span>
          </button>
        ))}
      </div>
      {!data ? <LoadingBlock /> : days.length === 0 ? <Panel><EmptyState title="Nothing happened in this window" body="Try a longer date range, more event types, or fewer filters." /></Panel> : (
        <Panel className="panel-flush">
          {days.map((d) => (
            <section key={d.day} className="tl-day">
              <h3 className="tl-day-head">{d.day}</h3>
              <ol className="tl">
                {d.events.map((e, i) => (
                  <li key={`${e.at}-${e.type}-${i}`} className={`tl-item tone-${tone(e.type) || 'none'}`}>
                    <span className="tl-time muted num">{timeShort(e.at)}</span>
                    <span className="tl-dot" aria-hidden="true" />
                    <div className="tl-body">
                      <div>{sentence(e)}</div>
                      {e.post_id && (
                        <Link to={`/leads/${e.post_id}?view=all`} className="tl-lead">
                          {e.summary || e.text_start || 'Open the lead'}
                          {e.category_name && e.type !== 'lead_found' ? <span className="muted"> · {e.category_name}</span> : null}
                          {e.group_name && e.type !== 'alert' ? <span className="muted"> · {e.group_name}</span> : null}
                        </Link>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          ))}
          {data.truncated && <p className="muted" style={{ padding: 'var(--s-4) var(--s-6)' }}>Showing the newest 400. Narrow the dates or filters for older ones.</p>}
        </Panel>
      )}
    </>
  )
}

type Sale = {
  id: string; post_id: string; sent_at: string; billed_at: string | null; billed_cents: number | null; notified_at: string | null
  outcome: string | null; state: 'sold' | 'waiting' | 'not_sold'; rep_status: string; responded_at: string | null; skip_reason: string | null
  rep_id: string; rep_name: string; buyer_id: string; buyer_name: string; current_price_cents: number | null
  summary: string | null; text_start: string | null; category_name: string | null; area_name: string | null; group_name: string | null
  email_status: string | null
}
type SoldData = { rows: Sale[]; totals: { sold: number; revenue_cents: number; unpriced: number; waiting: number; waiting_value_cents: number; not_sold: number; emailed: number; answered: number; contacted: number }; truncated: boolean }

const STATE: Record<Sale['state'], { label: string; tone: Tone }> = {
  sold: { label: 'Sold', tone: 'pos' }, waiting: { label: 'Waiting on rep', tone: 'warn' }, not_sold: { label: 'Rep skipped', tone: undefined },
}
const EMAIL: Record<string, string> = { sent: 'emailed', pending: 'email queued', failed: 'email failed', skipped: 'email not sent' }

function Sold({ query }: { query: string }) {
  const { data, error } = useApi<SoldData>(`/api/activity/sold?${query}`)
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const t = data.totals
  const cols: Column<Sale>[] = [
    { key: 'when', header: 'When', cell: (r) => <span className="nowrap muted">{ago(r.billed_at || r.sent_at)}</span>, sortBy: (r) => r.billed_at || r.sent_at },
    { key: 'lead', header: 'Lead', cell: (r) => <Link to={`/leads/${r.post_id}?view=all`}><strong>{r.summary || r.text_start || 'Open'}</strong><div className="muted nowrap">{[r.category_name, r.area_name, r.group_name].filter(Boolean).join(' · ')}</div></Link>, sortBy: (r) => r.category_name || '' },
    { key: 'buyer', header: 'Buyer', cell: (r) => <strong className="nowrap">{r.buyer_name}</strong>, sortBy: (r) => r.buyer_name },
    { key: 'rep', header: 'Rep', cell: (r) => <span className="nowrap">{r.rep_name}</span>, sortBy: (r) => r.rep_name },
    { key: 'state', header: 'State', cell: (r) => <Chip tone={STATE[r.state].tone} dot>{STATE[r.state].label}</Chip>, sortBy: (r) => r.state },
    { key: 'price', header: 'Price', numeric: true, cell: (r) => r.state === 'sold' ? (r.billed_cents != null ? money(r.billed_cents) : <span className="muted">none set</span>) : <span className="muted">{r.current_price_cents != null ? money(r.current_price_cents) : ''}</span>, sortBy: (r) => r.billed_cents ?? r.current_price_cents ?? -1 },
    { key: 'told', header: 'Buyer told', cell: (r) => r.state !== 'sold' ? <span className="muted">not yet</span> : r.notified_at ? <span className="nowrap">{ago(r.notified_at)}</span> : <span className={r.email_status === 'failed' ? 'tone-neg nowrap' : 'muted nowrap'}>{EMAIL[r.email_status || ''] || 'not emailed'}</span>, sortBy: (r) => r.notified_at || '' },
    { key: 'outcome', header: 'Buyer says', cell: (r) => r.outcome === 'contacted' ? <Chip tone="pos">Reached out</Chip> : r.outcome === 'not_yet' ? <Chip>Not yet</Chip> : <span className="muted">no answer</span>, sortBy: (r) => r.outcome || '' },
  ]
  return (
    <>
      <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
        <StatTile focal label="Sold" value={num(t.sold)} meta={`${money(t.revenue_cents)} billed${t.unpriced ? `, ${t.unpriced} with no price set` : ''}`} />
        <StatTile label="Waiting on a rep" value={num(t.waiting)} meta={t.waiting_value_cents ? `about ${money(t.waiting_value_cents)} at today's prices` : 'nothing waiting'} />
        <StatTile label="Buyers told" value={t.sold ? `${Math.round((t.emailed / t.sold) * 100)}%` : 'none'} meta={`${t.emailed} of ${t.sold} sales emailed`} progress={t.sold ? (t.emailed / t.sold) * 100 : undefined} />
        <StatTile label="Buyer heard from the person" value={t.answered ? `${t.contacted} of ${t.answered}` : 'no answers'} meta={`${t.not_sold} lost to a skip`} />
      </div>
      <Panel className="panel-flush">
        <DataTable columns={cols} rows={data.rows} rowKey={(r) => r.id} empty={<EmptyState title="No buyers on any lead in this window" />} />
        {data.truncated && <p className="muted" style={{ padding: 'var(--s-4) var(--s-6)' }}>Showing the newest 1,000.</p>}
      </Panel>
    </>
  )
}

type Pair = {
  rep_id: string; rep_name: string; rep_active: boolean; buyer_id: string; buyer_name: string; buyer_status: string; category_name: string | null
  linked: boolean; sent: number; sold: number; waiting: number; not_sold: number; revenue_cents: number; contacted: number; answered: number
  last_at: string | null; median_reply_s: number | null
}

function Pairs({ query }: { query: string }) {
  const { data, error } = useApi<{ pairs: Pair[] }>(`/api/activity/pairs?${query}`)
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const cols: Column<Pair>[] = [
    { key: 'rep', header: 'Rep', cell: (r) => <strong className="nowrap">{r.rep_name}{!r.rep_active && <span className="muted"> (inactive)</span>}</strong>, sortBy: (r) => r.rep_name },
    { key: 'buyer', header: 'Buyer', cell: (r) => <><span className="nowrap">{r.buyer_name}</span>{r.buyer_status !== 'active' && <span className="muted"> ({r.buyer_status})</span>}<div className="muted nowrap">{r.category_name}{!r.linked && <> · <span className="tone-warn">no longer linked to this rep</span></>}</div></>, sortBy: (r) => r.buyer_name },
    { key: 'sent', header: 'Leads', numeric: true, cell: (r) => r.sent || <span className="muted">0</span>, sortBy: (r) => r.sent },
    { key: 'sold', header: 'Sold', numeric: true, cell: (r) => r.sold || <span className="muted">0</span>, sortBy: (r) => r.sold },
    { key: 'waiting', header: 'Waiting', numeric: true, cell: (r) => r.waiting ? <Chip tone="warn">{r.waiting}</Chip> : <span className="muted">0</span>, sortBy: (r) => r.waiting },
    { key: 'rev', header: 'Billed', numeric: true, cell: (r) => r.revenue_cents ? money(r.revenue_cents) : <span className="muted">$0</span>, sortBy: (r) => r.revenue_cents },
    { key: 'reply', header: 'Typical reply', numeric: true, cell: (r) => <span className="nowrap">{duration(r.median_reply_s)}</span>, sortBy: (r) => r.median_reply_s ?? 1e12 },
    { key: 'heard', header: 'Buyer heard', cell: (r) => <RateBar num={r.contacted} den={r.answered} />, sortBy: (r) => r.answered ? r.contacted / r.answered : -1 },
    { key: 'last', header: 'Last activity', cell: (r) => r.last_at ? <span className="nowrap muted">{ago(r.last_at)}</span> : <span className="muted">never</span>, sortBy: (r) => r.last_at || '' },
  ]
  const idle = data.pairs.filter((p) => p.linked && p.sent === 0).length
  return (
    <>
      {idle > 0 && <p className="muted" style={{ margin: '0 0 var(--s-4)' }}>{idle} pairing{idle === 1 ? ' has' : 's have'} produced no leads in this window.</p>}
      <Panel className="panel-flush">
        <DataTable columns={cols} rows={data.pairs} rowKey={(r) => `${r.rep_id}-${r.buyer_id}`}
                   empty={<EmptyState title="No reps carry any buyers yet" body="Link reps to buyers on the Subscribers page." />} />
      </Panel>
    </>
  )
}

export default function Activity() {
  const [params, setParams] = useSearchParams()
  const tab = (['timeline', 'sold', 'pairs'].includes(params.get('tab') || '') ? params.get('tab') : 'timeline') as Tab
  const f = useLeadFilters(tab === 'timeline' ? '7' : '30')
  const options = useFilterOptions()
  const setTab = (t: Tab) => { const n = new URLSearchParams(params); n.set('tab', t); setParams(n, { replace: true }) }
  const types = params.get('types') != null ? (params.get('types') || '').split(',').filter(Boolean) : DEFAULT_TYPES
  const setTypes = (t: string[]) => { const n = new URLSearchParams(params); n.set('types', t.join(',')); setParams(n, { replace: true }) }

  return (
    <>
      <PageHeader title="Activity" subtitle="Everything that happened to every lead: who spotted it, which rep has it, when they replied, who bought it, and what the buyer said." />
      <Tabs<Tab> value={tab} onChange={setTab} label="Activity views" tabs={[
        { value: 'timeline', label: 'Timeline' },
        { value: 'sold', label: 'Sold leads' },
        { value: 'pairs', label: 'Reps and buyers' },
      ]} />
      <div style={{ height: 'var(--s-5)' }} />
      <LeadFilterBar f={f} options={options} only={tab === 'pairs' ? ['days', 'category', 'rep', 'buyer'] : undefined} />
      {tab === 'timeline' && <Timeline query={f.query} types={types} setTypes={setTypes} />}
      {tab === 'sold' && <Sold query={f.query} />}
      {tab === 'pairs' && <Pairs query={f.query} />}
    </>
  )
}
